/**
 * AdminObsBroadcasts — full OBS control surface for production teams.
 *
 * Route: /admin/obs (admin auth).
 *
 * Features
 * --------
 *   • List + create OBS WebSocket connections (host/port/password).
 *   • Per-connection status card: live stream + record indicators, duration.
 *   • Scene switcher (one-click buttons per scene fetched from OBS).
 *   • Stream / Record start/stop controls (with audit trail in backend).
 *   • Text-source push (sermon title / lower-third / scripture reference).
 *   • Recent audit log per connection.
 *
 * Uses existing /api/obs/* endpoints from routes/obs_broadcasts.py.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Video, Plus, Play, Square, Radio, Circle, Wifi, WifiOff, Type, Trash2, RefreshCw,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const ConnectionStatus = ({ conn }) => {
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({
    queryKey: ['obs-status', conn.id],
    queryFn: async () => (await axios.get(
      `${API}/api/obs/connections/${conn.id}/status`, { headers: authH() }
    )).data,
    refetchInterval: 4000,
  });

  const call = async (path, msg) => {
    try {
      const { data } = await axios.post(
        `${API}/api/obs/connections/${conn.id}${path}`, {}, { headers: authH() }
      );
      if (data?.success === false) throw new Error(data?.error || 'OBS error');
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ['obs-status', conn.id] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const switchScene = async (scene) => {
    try {
      await axios.post(
        `${API}/api/obs/connections/${conn.id}/scene`,
        { scene_name: scene }, { headers: authH() }
      );
      toast.success(`Scene → ${scene}`);
      qc.invalidateQueries({ queryKey: ['obs-status', conn.id] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const online = status?.success !== false;
  const streaming = status?.streaming;
  const recording = status?.recording;

  return (
    <Card className="bg-slate-900/60 border-slate-800 text-slate-200"
            data-testid={`obs-conn-card-${conn.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base text-white flex items-center gap-2">
              {online
                ? <Wifi className="h-4 w-4 text-emerald-400" />
                : <WifiOff className="h-4 w-4 text-rose-400" />}
              {conn.label}
            </CardTitle>
            <div className="text-xs text-slate-500 mt-1">
              {conn.host}:{conn.port} {conn.site_id && `· site ${conn.site_id}`}
            </div>
          </div>
          <div className="flex gap-1.5">
            {streaming && <Badge className="bg-rose-500/20 text-rose-200 border-rose-400/40">LIVE</Badge>}
            {recording && <Badge className="bg-amber-500/20 text-amber-200 border-amber-400/40">REC</Badge>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Scenes */}
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500 mb-1.5 block">Scenes</Label>
          {!online && <div className="text-xs text-rose-400">{status?.error || 'OBS offline'}</div>}
          <div className="flex flex-wrap gap-1.5">
            {(status?.scenes || []).map(s => (
              <Button key={s} variant="outline" size="sm"
                       onClick={() => switchScene(s)}
                       className={`text-xs ${s === status?.current_scene
                         ? 'bg-indigo-500/30 border-indigo-400 text-indigo-100'
                         : 'border-slate-700 text-slate-300'}`}
                       data-testid={`obs-scene-${s}`}>
                {s === status?.current_scene && '● '}{s}
              </Button>
            ))}
            {online && (status?.scenes || []).length === 0 && (
              <div className="text-xs text-slate-500">No scenes loaded</div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-2 gap-2">
          <Button size="sm" variant={streaming ? 'destructive' : 'default'}
                   disabled={!online} onClick={() => call(streaming ? '/stream/stop' : '/stream/start',
                     streaming ? 'Stream stopped' : 'Stream started')}
                   data-testid={`obs-stream-${streaming ? 'stop' : 'start'}`}>
            <Radio className="h-4 w-4 mr-2" />
            {streaming ? 'Stop stream' : 'Start stream'}
          </Button>
          <Button size="sm" variant={recording ? 'destructive' : 'secondary'}
                   disabled={!online} onClick={() => call(recording ? '/record/stop' : '/record/start',
                     recording ? 'Recording stopped' : 'Recording started')}
                   data-testid={`obs-record-${recording ? 'stop' : 'start'}`}>
            <Circle className={`h-4 w-4 mr-2 ${recording ? 'animate-pulse text-rose-400' : ''}`} />
            {recording ? 'Stop record' : 'Start record'}
          </Button>
        </div>

        <TextSourcePush connectionId={conn.id} disabled={!online} />
        <ObsAudit connectionId={conn.id} />
      </CardContent>
    </Card>
  );
};

const TextSourcePush = ({ connectionId, disabled }) => {
  const [source, setSource] = useState('lower_third');
  const [text, setText] = useState('');
  const send = async () => {
    if (!text.trim()) return;
    try {
      await axios.post(
        `${API}/api/obs/connections/${connectionId}/text`,
        { source_name: source, text }, { headers: authH() }
      );
      toast.success(`Pushed → ${source}`);
      setText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };
  return (
    <div className="border-t border-slate-800 pt-3 space-y-2">
      <Label className="text-xs uppercase tracking-wide text-slate-500">Push text source</Label>
      <div className="flex gap-2">
        <Input value={source} onChange={e => setSource(e.target.value)}
               placeholder="OBS source name" className="bg-slate-800/60 border-slate-700 text-xs w-44"
               data-testid={`obs-text-source-${connectionId}`} />
        <Input value={text} onChange={e => setText(e.target.value)}
               placeholder="Sermon title / lower-third copy…"
               className="bg-slate-800/60 border-slate-700 text-xs"
               data-testid={`obs-text-content-${connectionId}`} />
        <Button size="sm" disabled={disabled || !text.trim()} onClick={send}
                 data-testid={`obs-text-push-${connectionId}`}>
          <Type className="h-3.5 w-3.5 mr-1.5" /> Push
        </Button>
      </div>
    </div>
  );
};

const ObsAudit = ({ connectionId }) => {
  const { data } = useQuery({
    queryKey: ['obs-audit', connectionId],
    queryFn: async () => (await axios.get(
      `${API}/api/obs/connections/${connectionId}/audit?limit=8`, { headers: authH() }
    )).data,
    refetchInterval: 10000,
  });
  const logs = data?.logs || [];
  return (
    <details className="border-t border-slate-800 pt-3 group">
      <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300 select-none">
        Recent activity ({logs.length})
      </summary>
      <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
        {logs.map(l => (
          <div key={l.id} className="text-[11px] text-slate-400 flex justify-between gap-2 border-b border-slate-800/60 pb-1">
            <span className="font-mono">{l.action}</span>
            <span className={`text-[10px] ${l.result === 'error' ? 'text-rose-400' : 'text-emerald-400'}`}>
              {l.result || 'ok'}
            </span>
            <span className="text-slate-600">{new Date(l.at).toLocaleTimeString()}</span>
          </div>
        ))}
        {logs.length === 0 && <div className="text-[11px] text-slate-600">No activity yet</div>}
      </div>
    </details>
  );
};

const AddConnectionDialog = ({ open, onOpenChange }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    label: '', host: '127.0.0.1', port: 4455, password: '',
    tenant_id: '', is_active: true,
  });
  const save = useMutation({
    mutationFn: async () => (await axios.post(
      `${API}/api/obs/connections`, form, { headers: authH() }
    )).data,
    onSuccess: () => {
      toast.success('Connection added');
      onOpenChange(false);
      qc.invalidateQueries({ queryKey: ['obs-conns'] });
      setForm({ label: '', host: '127.0.0.1', port: 4455, password: '', tenant_id: '', is_active: true });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Save failed'),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
        <DialogHeader>
          <DialogTitle>New OBS connection</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Set in OBS → Tools → WebSocket Server Settings. Default port 4455.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          {[
            ['label', 'Label (e.g. Main Sanctuary)'],
            ['host', 'Host'],
            ['port', 'Port'],
            ['password', 'Password (optional)'],
            ['tenant_id', 'Tenant ID'],
          ].map(([k, ph]) => (
            <div key={k}>
              <Label className="text-xs text-slate-500">{ph}</Label>
              <Input value={form[k]} onChange={e => setForm({ ...form, [k]: k === 'port' ? Number(e.target.value) : e.target.value })}
                     type={k === 'password' ? 'password' : 'text'}
                     className="bg-slate-800/60 border-slate-700"
                     data-testid={`obs-new-${k}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !form.label || !form.tenant_id}
                   data-testid="obs-new-save">
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminObsBroadcasts = () => {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['obs-conns'],
    queryFn: async () => (await axios.get(
      `${API}/api/obs/connections`, { headers: authH() }
    )).data,
  });
  const conns = data?.connections || [];

  const remove = useMutation({
    mutationFn: async (id) => axios.delete(
      `${API}/api/obs/connections/${id}`, { headers: authH() }
    ),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['obs-conns'] }); },
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white inline-flex items-center gap-2">
              <Video className="h-6 w-6 text-indigo-400" /> OBS Broadcast Control
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Switch scenes, start/stop streams + recordings, push lower-third text to OBS Studio over WebSocket.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={() => setAdding(true)} data-testid="obs-add-connection">
              <Plus className="h-4 w-4 mr-1.5" /> New connection
            </Button>
          </div>
        </header>

        <div className="grid lg:grid-cols-2 gap-4" data-testid="obs-conn-list">
          {isLoading && <div className="text-slate-500 col-span-2">Loading…</div>}
          {!isLoading && conns.length === 0 && (
            <Card className="bg-slate-900/60 border-slate-800 col-span-2">
              <CardContent className="p-10 text-center text-slate-400">
                No OBS connections yet. Click <strong>New connection</strong>,
                point it at the machine running OBS Studio with WebSocket plugin enabled.
              </CardContent>
            </Card>
          )}
          {conns.map(c => (
            <div key={c.id} className="relative group">
              <ConnectionStatus conn={c} />
              <Button variant="ghost" size="sm"
                       onClick={() => { if (window.confirm(`Delete "${c.label}"?`)) remove.mutate(c.id); }}
                       className="absolute top-3 right-3 text-slate-500 hover:text-rose-300 opacity-0 group-hover:opacity-100 transition"
                       data-testid={`obs-delete-${c.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>

        <AddConnectionDialog open={adding} onOpenChange={setAdding} />
      </div>
    </div>
  );
};

export default AdminObsBroadcasts;
